// src/components/navigation/UserProfileDropdown.tsx
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  UserCircleIcon, 
  Cog6ToothIcon, 
  ShieldCheckIcon, 
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  ServerStackIcon,
  ServerIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { User } from '../../features/auth/types';

interface UserProfileDropdownProps {
  user: User;
  has2FA: boolean;
  onLogout: () => void;
  onOpenSecuritySettings: () => void;
  onOpenProfile: () => void;
  onOpenPlatformCredentials: () => void;
  onOpenCacheSettings: () => void;
  onOpenOrganizationSettings: () => void;
}

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({
  user,
  has2FA,
  onLogout,
  onOpenSecuritySettings,
  onOpenProfile,
  onOpenPlatformCredentials,
  onOpenCacheSettings,
  onOpenOrganizationSettings
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update button position when dropdown opens
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      setButtonRect(buttonRef.current.getBoundingClientRect());
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleProfileSettings = () => {
    console.log('Profile Settings clicked');
    setIsOpen(false);
    onOpenProfile();
  };

  const handleSecuritySettings = () => {
    console.log('Security Settings clicked');
    setIsOpen(false);
    onOpenSecuritySettings();
  };

  const handlePlatformCredentials = () => {
    console.log('Platform Credentials clicked');
    setIsOpen(false);
    onOpenPlatformCredentials();
  };

  const handleCacheSettings = () => {
    console.log('Cache Settings clicked');
    setIsOpen(false);
    onOpenCacheSettings();
  };

  const handleOrganizationSettings = () => {
    console.log('Organization Settings clicked');
    setIsOpen(false);
    onOpenOrganizationSettings();
  };

  const handleLogout = () => {
    console.log('Logout clicked');
    setIsOpen(false);
    onLogout();
  };

  return (
    <>
      {/* Profile Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-2 rounded-md text-theme-text dark:text-dark-theme-text hover:bg-theme-bg-secondary dark:hover:bg-dark-theme-bg-secondary transition-colors duration-200"
      >
        <div className="flex items-center space-x-2">
          <UserCircleIcon className="h-6 w-6" />
          <span className="text-sm font-medium hidden sm:block">
            {user.first_name} {user.last_name}
          </span>
          {has2FA && (
            <ShieldCheckIcon className="h-4 w-4 text-green-500" title="2FA Enabled" />
          )}
        </div>
        <ChevronDownIcon 
          className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      
      {/* Dropdown Menu using Portal */}
      {isOpen && buttonRect && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed w-64 bg-theme-bg-card dark:bg-dark-theme-bg-card rounded-lg shadow-lg border border-theme-border dark:border-dark-theme-border z-[999]"
          style={{
            top: buttonRect.bottom + 4,
            right: window.innerWidth - buttonRect.right,
          }}
        >
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <UserCircleIcon className="h-8 w-8 text-gray-400" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user.first_name} {user.last_name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user.email}
                </p>
                {user.group_details?.name && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {user.group_details.name}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {/* Profile Settings */}
            <div
              onClick={handleProfileSettings}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center space-x-3"
            >
              <UserCircleIcon className="h-4 w-4" />
              <span>Profile Settings</span>
            </div>

            {/* Security Settings */}
            <div
              onClick={handleSecuritySettings}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center justify-between"
            >
              <div className="flex items-center space-x-3">
                <ShieldCheckIcon className="h-4 w-4" />
                <span>Security Settings</span>
              </div>
              {/* Commented out setup reminder badge - redundant due to post-login credential check
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                has2FA 
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
              }`}>
                {has2FA ? 'Enabled' : 'Setup'}
              </span>
              */}
            </div>

            {/* Platform Credentials */}
            <div
              onClick={handlePlatformCredentials}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center space-x-3"
            >
              <ServerStackIcon className="h-4 w-4" />
              <span>Platform Credentials</span>
            </div>

            {/* Organization Settings - Only visible to staff users */}
            {user.is_staff && (
              <div
                onClick={handleOrganizationSettings}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center space-x-3"
              >
                <BuildingOfficeIcon className="h-4 w-4" />
                <span>Organization Settings</span>
              </div>
            )}

            {/* Cache Settings - Only visible to staff users */}
            {user.is_staff && (
              <div
                onClick={handleCacheSettings}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center space-x-3"
              >
                <ServerIcon className="h-4 w-4" />
                <span>Cache Settings</span>
              </div>
            )}
          </div>

          {/* Logout */}
          <div className="border-t border-gray-200 dark:border-gray-700">
            <div
              onClick={handleLogout}
              className="px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 cursor-pointer flex items-center space-x-3"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4" />
              <span>Sign Out</span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default UserProfileDropdown;